/**
 * wineryContact — build "always-current" contact URLs for a winery.
 *
 * The goal: never show a stale hardcoded phone number. Instead, generate
 * a Google Maps deep-link that resolves to Google's live business listing
 * — which has the current phone, hours, address, and website. On mobile,
 * tapping the Maps result triggers the native app with a one-tap Call
 * button. On desktop, it opens the web listing.
 *
 * Optional override: if a `phone` field is provided in the source data,
 * we render a direct `tel:` link alongside the Maps deep-link.
 *
 * Usage:
 *   const { telHref, mapsHref } = wineryContact({ name, region, phone });
 */
export function wineryContact({ name, region = '', phone = null }) {
  const query = `${name} winery${region ? ' ' + region : ''}`;
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  const normalized = phone ? normalizePhone(phone) : null;
  const telHref = normalized ? `tel:${normalized}` : null;
  return { telHref, mapsHref, displayPhone: normalized ? phone : null };
}

/**
 * normalizePhone — accept 415-555-1234 / (415) 555-1234 / 4155551234 /
 * +1 415 555 1234 and return E.164-ish digits suitable for `tel:`.
 * Returns null for anything that can't be normalized to a valid number
 * so the caller can safely fall back to the Google Maps deep-link.
 */
function normalizePhone(raw) {
  const cleaned = String(raw).replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+') && cleaned.length >= 11) return cleaned;
  if (cleaned.length === 10) return `+1${cleaned}`;
  if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
  return null;
}
